/**
 * AdminContext — React context for managing admin page state:
 * system settings, user management, and IdP status.
 *
 * Requirements: 14.1, 14.2, 14.3, 14.4
 */

import React, { createContext, useCallback, useContext, useEffect, useReducer } from 'react';
import * as api from '../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SystemSettings {
  readonly siteName: string;
  readonly registrationEnabled: boolean;
  readonly defaultBoardVisibility: 'private' | 'org' | 'public';
}

export interface AdminUser {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string;
  readonly role: 'admin' | 'user';
  readonly enabled: boolean;
  readonly lastLogin: string | null;
}

export interface IdpStatus {
  readonly type: string;
  readonly endpoint: string;
  readonly status: 'connected' | 'disconnected' | 'unknown';
}

export interface AdminState {
  readonly settings: SystemSettings | null;
  readonly users: AdminUser[];
  readonly idpStatus: IdpStatus | null;
  readonly bootstrapMode: boolean;
  readonly isLoading: boolean;
  readonly error: string | null;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type AdminAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SETTINGS_SUCCESS'; payload: SystemSettings }
  | { type: 'FETCH_USERS_SUCCESS'; payload: AdminUser[] }
  | { type: 'FETCH_IDP_STATUS_SUCCESS'; payload: IdpStatus }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'UPDATE_SETTINGS_OPTIMISTIC'; payload: Partial<SystemSettings> }
  | { type: 'UPDATE_SETTINGS_REVERT'; payload: SystemSettings }
  | { type: 'UPDATE_SETTINGS_CONFIRMED' }
  | { type: 'UPDATE_USER_OPTIMISTIC'; payload: { userId: string; updates: Partial<AdminUser> } }
  | { type: 'UPDATE_USER_REVERT'; payload: AdminUser[] }
  | { type: 'UPDATE_USER_CONFIRMED' }
  | { type: 'SET_BOOTSTRAP_MODE'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export const initialAdminState: AdminState = {
  settings: null,
  users: [],
  idpStatus: null,
  bootstrapMode: false,
  isLoading: false,
  error: null,
};

export function adminReducer(state: AdminState, action: AdminAction): AdminState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null };

    case 'FETCH_SETTINGS_SUCCESS':
      return { ...state, isLoading: false, settings: action.payload };

    case 'FETCH_USERS_SUCCESS':
      return { ...state, isLoading: false, users: action.payload };

    case 'FETCH_IDP_STATUS_SUCCESS':
      return { ...state, isLoading: false, idpStatus: action.payload };

    case 'FETCH_ERROR':
      return { ...state, isLoading: false, error: action.payload };

    case 'UPDATE_SETTINGS_OPTIMISTIC':
      if (!state.settings) return state;
      return { ...state, settings: { ...state.settings, ...action.payload }, error: null };

    case 'UPDATE_SETTINGS_REVERT':
      return { ...state, settings: action.payload };

    case 'UPDATE_SETTINGS_CONFIRMED':
      return state;

    case 'UPDATE_USER_OPTIMISTIC': {
      const updatedUsers = state.users.map((u) =>
        u.userId === action.payload.userId ? { ...u, ...action.payload.updates } : u,
      );
      return { ...state, users: updatedUsers, error: null };
    }

    case 'UPDATE_USER_REVERT':
      return { ...state, users: action.payload };

    case 'UPDATE_USER_CONFIRMED':
      return state;

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_BOOTSTRAP_MODE':
      return { ...state, bootstrapMode: action.payload };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface AdminContextValue {
  readonly state: AdminState;
  fetchSettings: () => Promise<void>;
  fetchUsers: () => Promise<void>;
  updateSettings: (updates: Partial<SystemSettings>) => Promise<void>;
  updateUser: (userId: string, updates: { role?: 'admin' | 'user'; enabled?: boolean }) => Promise<void>;
}

const AdminContext = createContext<AdminContextValue | null>(null);

// ---------------------------------------------------------------------------
// API response types
// ---------------------------------------------------------------------------

interface SettingsResponse {
  readonly data: {
    readonly mode: 'bootstrap' | 'configured';
    readonly configured: boolean;
  };
  readonly siteName?: string;
  readonly registrationEnabled?: boolean;
  readonly defaultBoardVisibility?: 'private' | 'org' | 'public';
  readonly idp?: {
    readonly type: string;
    readonly endpoint: string;
    readonly status: 'connected' | 'disconnected' | 'unknown';
  };
}

interface UsersResponse {
  readonly users: AdminUser[];
}

// ---------------------------------------------------------------------------
// Provider component
// ---------------------------------------------------------------------------

interface AdminProviderProps {
  readonly children: React.ReactNode;
  readonly skipInitialFetch?: boolean;
}

export function AdminProvider({ children, skipInitialFetch }: AdminProviderProps): React.ReactElement {
  const [state, dispatch] = useReducer(adminReducer, initialAdminState);

  const fetchSettings = useCallback(async (): Promise<void> => {
    dispatch({ type: 'FETCH_START' });
    try {
      const response = await api.get<SettingsResponse>('/v1/admin/settings');

      // Detect bootstrap mode from BFF response
      const isBootstrap = response.data
        ? response.data.mode === 'bootstrap' || !response.data.configured
        : !response.idp;
      dispatch({ type: 'SET_BOOTSTRAP_MODE', payload: isBootstrap });

      // Extract settings if present in legacy shape
      if (response.siteName !== undefined) {
        dispatch({
          type: 'FETCH_SETTINGS_SUCCESS',
          payload: {
            siteName: response.siteName,
            registrationEnabled: response.registrationEnabled ?? false,
            defaultBoardVisibility: response.defaultBoardVisibility ?? 'private',
          },
        });
      } else {
        dispatch({ type: 'FETCH_SETTINGS_SUCCESS', payload: { siteName: '', registrationEnabled: false, defaultBoardVisibility: 'private' } });
      }

      if (response.idp) {
        dispatch({
          type: 'FETCH_IDP_STATUS_SUCCESS',
          payload: {
            type: response.idp.type,
            endpoint: response.idp.endpoint,
            status: response.idp.status,
          },
        });
      }
    } catch (error: unknown) {
      const message = extractErrorMessage(error);
      dispatch({ type: 'FETCH_ERROR', payload: message });
    }
  }, []);

  const fetchUsers = useCallback(async (): Promise<void> => {
    dispatch({ type: 'FETCH_START' });
    try {
      const response = await api.get<{ data: UsersResponse } | UsersResponse>('/v1/admin/users');
      // Handle both wrapped ({ data: { users } }) and unwrapped ({ users }) response shapes
      const users = 'data' in response && response.data && 'users' in (response.data as object)
        ? (response.data as UsersResponse).users
        : (response as UsersResponse).users ?? [];
      dispatch({ type: 'FETCH_USERS_SUCCESS', payload: users });
    } catch (error: unknown) {
      const message = extractErrorMessage(error);
      dispatch({ type: 'FETCH_ERROR', payload: message });
    }
  }, []);

  const updateSettings = useCallback(async (updates: Partial<SystemSettings>): Promise<void> => {
    const previousSettings = state.settings;
    if (!previousSettings) return;

    dispatch({ type: 'UPDATE_SETTINGS_OPTIMISTIC', payload: updates });
    try {
      await api.put('/v1/admin/settings', updates);
      dispatch({ type: 'UPDATE_SETTINGS_CONFIRMED' });
    } catch (error: unknown) {
      dispatch({ type: 'UPDATE_SETTINGS_REVERT', payload: previousSettings });
      const message = extractErrorMessage(error);
      dispatch({ type: 'SET_ERROR', payload: message });
    }
  }, [state.settings]);

  const updateUser = useCallback(async (
    userId: string,
    updates: { role?: 'admin' | 'user'; enabled?: boolean },
  ): Promise<void> => {
    const previousUsers = state.users;

    dispatch({ type: 'UPDATE_USER_OPTIMISTIC', payload: { userId, updates } });
    try {
      await api.put(`/v1/admin/users/${userId}`, updates);
      dispatch({ type: 'UPDATE_USER_CONFIRMED' });
    } catch (error: unknown) {
      dispatch({ type: 'UPDATE_USER_REVERT', payload: previousUsers });
      const message = extractErrorMessage(error);
      dispatch({ type: 'SET_ERROR', payload: message });
    }
  }, [state.users]);

  // Fetch data on mount
  useEffect(() => {
    if (!skipInitialFetch) {
      fetchSettings();
      fetchUsers();
    }
  }, [fetchSettings, fetchUsers, skipInitialFetch]);

  const value: AdminContextValue = {
    state,
    fetchSettings,
    fetchUsers,
    updateSettings,
    updateUser,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAdmin(): AdminContextValue {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function extractErrorMessage(error: unknown): string {
  if (api.isClientError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
